import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import React, {useEffect, useRef, useState} from 'react';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {ERROR} from '@di-shared/const';
import clsx from 'clsx';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import LottieSelector from '@di-shared/features/common/LottieSelector';
import DailyLineGraphError from '@di-shared/features/common/DailyLineGraphError';
import useUnitConverter from '@di-shared/utils/useUnitConverter';

enum TripTab {
  SAFE_DRIVING,
  RECKLESS_DRIVING,
}

function SafeDrivingContentsErrorKr() {
  const {t} = useInferableTranslation();
  const [selectedTripTab, setSelectedTripTab] = useState<TripTab>(TripTab.RECKLESS_DRIVING);
  const initialSelectedButtonRef = useRef<HTMLButtonElement>(null);
  const predictGraphBarRef = useRef<HTMLSpanElement>(null);
  const [isOpenScoreStandard, setIsOpenScoreStandard] = useState(false);
  const [predictIndex, setPredictIndex] = useState(0);
  const [predictBarWidth, setPredictBarWidth] = useState<string | number>(0);
  const {convertDistanceValue, convertDistanceSymbol, defaultDistanceSymbol} = useUnitConverter();

  function changeSelectedScore(targetWidth: number, targetOffsetLeft: number, index: number) {
    if (predictGraphBarRef.current) {
      const graphBar = predictGraphBarRef.current;
      const barWidth = index === 3 ? '100%' : Math.round(targetWidth / 2 + targetOffsetLeft - graphBar.offsetLeft + 4);

      setPredictIndex(index);
      setPredictBarWidth(barWidth);
    }
  }

  useEffect(() => {
    const target = initialSelectedButtonRef.current;
    if (target) {
      changeSelectedScore(target.offsetWidth, target.offsetLeft, 0);
    }
  }, []);

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('T40004')}</h1>
      </Header>
      <main className="content page_safe_driving">
        {/* TODO : 스펙 아웃 */}
        {/*<div className="guide_banner">*/}
        {/*  <p className="guide_banner_desc">*/}
        {/*    <span className="ic ic_filled_caution_16px"></span>*/}
        {/*    {t('T20124')}*/}
        {/*  </p>*/}
        {/*</div>*/}
        <section className="section section_safe_driving">
          <h2 className="blind">{t('T20125')}</h2>
          <div className="my_driving_score">
            <div className="my_score_box">
              <div className="my_score_imgbox">
                <LottieSelector
                  id={'safe_driving_score_good_light'}
                  kiaLightSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good_light.json')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good.json')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good_light.json')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good.json')}
                  genesisLightSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good_light.json')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good.json')}
                  loop={true}
                  autoplay={true}
                />
              </div>
              <div className="my_score_info">
                <div className="my_score_point">
                  <span className="my_score_value">{ERROR.DASH}</span>
                  <span className="my_score_unit">{t('T10056')}</span>
                </div>
                <span className="my_score_update">{t('T10077', {date: ERROR.DASH})}</span>
              </div>
            </div>
            <div className="score_factor">
              <ul className="score_factor_list">
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('T10011')}</span>
                  <span className="score_factor_value">{t('T10014_$count', {count: ERROR.DASH})}</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('T10012')}</span>
                  <span className="score_factor_value">{t('T10014_$count', {count: ERROR.DASH})}</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('T10013')}</span>
                  <span className="score_factor_value">{t('T10014_$count', {count: ERROR.DASH})}</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('T20000')}</span>
                  <span className="score_factor_value">{t('T10014_$count', {count: ERROR.DASH})}</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="section_container safe_driving_standard">
            <div className="driving_accordion type_container">
              <div className="driving_accordion_item">
                <h3>
                  <button
                    type="button"
                    className="driving_accordion_btn"
                    aria-expanded={isOpenScoreStandard}
                    aria-controls="accordion_content01"
                    onClick={() => {
                      setIsOpenScoreStandard((prev) => !prev);
                    }}
                  >
                    <span className="driving_accordion_title">{t('T20126')}</span>
                    <span
                      className="ic ic_arrow_down_16px"
                      aria-hidden="true"
                    ></span>
                  </button>
                </h3>
              </div>
            </div>
          </div>
          <div className="section_container safe_driving_daily">
            <div className="section_container_header">
              <h3 className="title_xxsmall">{t('T20009')}</h3>
            </div>
            <DailyLineGraphError description={t('T20187')} />

            <ul className="statistics_info_list">
              <li className="statistics_info_item">
                <span className="statistics_info_key">{t('T20010')}</span>
                <span className="statistics_info_val">
                  <span className="statistics_info_number">{ERROR.DASH}</span>
                  <span className="statistics_info_unit">{t('T10056')}</span>
                </span>
              </li>
              <li className="statistics_info_item">
                <span className="statistics_info_key">{t('T20011')}</span>
                <span className="statistics_info_val">
                  <span className="statistics_info_number">{ERROR.DASH}</span>
                  <span className="statistics_info_unit">{t('T10056')}</span>
                </span>
              </li>
            </ul>
          </div>
          <div className="section_container safe_driving_major">
            <div className="section_container_header">
              <h3 className="title_xxsmall">{t('T20013')}</h3>
            </div>
            <p className="section_container_desc">{t('T20014')}</p>

            <div className="tabbar type_brick">
              <button
                type="button"
                className={clsx('tabbar_item body_small_b', {'is-selected': selectedTripTab === TripTab.RECKLESS_DRIVING})}
                onClick={() => setSelectedTripTab(TripTab.RECKLESS_DRIVING)}
              >
                {t('T20015')}
              </button>
              <button
                type="button"
                className={clsx('tabbar_item body_small_b', {'is-selected': selectedTripTab === TripTab.SAFE_DRIVING})}
                onClick={() => setSelectedTripTab(TripTab.SAFE_DRIVING)}
              >
                {t('T20016')}
              </button>
            </div>
            {selectedTripTab === TripTab.RECKLESS_DRIVING && (
              <div className="tab_content driving_danger">
                <ul className="major_driving_list">
                  <li className="major_driving_item">
                    <div className={'major_driving_link'}>
                      <strong className="major_driving_rank">
                        <span className="rank_text">{t('T20017_$n', {n: ERROR.DASH})}</span>
                        <span className="count_text">
                          <span className="count_value">{ERROR.DASH}</span>
                          <span className="count_unit">{t('T20133')}</span>
                        </span>
                      </strong>
                      <div className="major_driving_info">
                        <div className="major_driving_action">
                          <span className="driving_action">
                            <span className="action_text">{t('T10011')}</span>
                            <span className="action_value">{ERROR.DASH}</span>
                          </span>
                          <span className="divider divider_vertical"></span>
                          <span className="driving_action">
                            <span className="action_text">{t('T10012')}</span>
                            <span className="action_value">{ERROR.DASH}</span>
                          </span>
                          <span className="divider divider_vertical"></span>
                          <span className="driving_action">
                            <span className="action_text">{t('T10013')}</span>
                            <span className="action_value">{ERROR.DASH}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                  <li className="major_driving_item">
                    <div className={'major_driving_link'}>
                      <strong className="major_driving_rank">
                        <span className="rank_text">{t('T20017_$n', {n: ERROR.DASH})}</span>
                        <span className="count_text">
                          <span className="count_value">{ERROR.DASH}</span>
                          <span className="count_unit">{t('T20133')}</span>
                        </span>
                      </strong>
                      <div className="major_driving_info">
                        <div className="major_driving_action">
                          <span className="driving_action">
                            <span className="action_text">{t('T10011')}</span>
                            <span className="action_value">{ERROR.DASH}</span>
                          </span>
                          <span className="divider divider_vertical"></span>
                          <span className="driving_action">
                            <span className="action_text">{t('T10012')}</span>
                            <span className="action_value">{ERROR.DASH}</span>
                          </span>
                          <span className="divider divider_vertical"></span>
                          <span className="driving_action">
                            <span className="action_text">{t('T10013')}</span>
                            <span className="action_value">{ERROR.DASH}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                  <li className="major_driving_item">
                    <div className={'major_driving_link'}>
                      <strong className="major_driving_rank">
                        <span className="rank_text">{t('T20017_$n', {n: ERROR.DASH})}</span>
                        <span className="count_text">
                          <span className="count_value">{ERROR.DASH}</span>
                          <span className="count_unit">{t('T20133')}</span>
                        </span>
                      </strong>
                      <div className="major_driving_info">
                        <div className="major_driving_action">
                          <span className="driving_action">
                            <span className="action_text">{t('T10011')}</span>
                            <span className="action_value">{ERROR.DASH}</span>
                          </span>
                          <span className="divider divider_vertical"></span>
                          <span className="driving_action">
                            <span className="action_text">{t('T10012')}</span>
                            <span className="action_value">{ERROR.DASH}</span>
                          </span>
                          <span className="divider divider_vertical"></span>
                          <span className="driving_action">
                            <span className="action_text">{t('T10013')}</span>
                            <span className="action_value">{ERROR.DASH}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            )}
            {selectedTripTab === TripTab.SAFE_DRIVING && (
              <div className="tab_content driving_safety">
                <ul className="major_driving_list">
                  <li className="major_driving_item">
                    <div className={'major_driving_link'}>
                      <strong className="major_driving_rank">
                        <span className="rank_text">{t('T20017_$n', {n: ERROR.DASH})}</span>
                        <span className="count_text">
                          <span className="count_value">{ERROR.DASH}</span>
                          <span className="count_unit">{t('T20133')}</span>
                        </span>
                      </strong>
                      <div className="major_driving_info">
                        <div className="major_driving_action">
                          <span className="driving_action">
                            <span className="action_text">{t('T10011')}</span>
                            <span className="action_value">{ERROR.DASH}</span>
                          </span>
                          <span className="divider divider_vertical"></span>
                          <span className="driving_action">
                            <span className="action_text">{t('T10012')}</span>
                            <span className="action_value">{ERROR.DASH}</span>
                          </span>
                          <span className="divider divider_vertical"></span>
                          <span className="driving_action">
                            <span className="action_text">{t('T10013')}</span>
                            <span className="action_value">{ERROR.DASH}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                  <li className="major_driving_item">
                    <div className={'major_driving_link'}>
                      <strong className="major_driving_rank">
                        <span className="rank_text">{t('T20017_$n', {n: ERROR.DASH})}</span>
                        <span className="count_text">
                          <span className="count_value">{ERROR.DASH}</span>
                          <span className="count_unit">{t('T20133')}</span>
                        </span>
                      </strong>
                      <div className="major_driving_info">
                        <div className="major_driving_action">
                          <span className="driving_action">
                            <span className="action_text">{t('T10011')}</span>
                            <span className="action_value">{ERROR.DASH}</span>
                          </span>
                          <span className="divider divider_vertical"></span>
                          <span className="driving_action">
                            <span className="action_text">{t('T10012')}</span>
                            <span className="action_value">{ERROR.DASH}</span>
                          </span>
                          <span className="divider divider_vertical"></span>
                          <span className="driving_action">
                            <span className="action_text">{t('T10013')}</span>
                            <span className="action_value">{ERROR.DASH}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                  <li className="major_driving_item">
                    <div className={'major_driving_link'}>
                      <strong className="major_driving_rank">
                        <span className="rank_text">{t('T20017_$n', {n: ERROR.DASH})}</span>
                        <span className="count_text">
                          <span className="count_value">{ERROR.DASH}</span>
                          <span className="count_unit">{t('T20133')}</span>
                        </span>
                      </strong>
                      <div className="major_driving_info">
                        <div className="major_driving_action">
                          <span className="driving_action">
                            <span className="action_text">{t('T10011')}</span>
                            <span className="action_value">{ERROR.DASH}</span>
                          </span>
                          <span className="divider divider_vertical"></span>
                          <span className="driving_action">
                            <span className="action_text">{t('T10012')}</span>
                            <span className="action_value">{ERROR.DASH}</span>
                          </span>
                          <span className="divider divider_vertical"></span>
                          <span className="driving_action">
                            <span className="action_text">{t('T10013')}</span>
                            <span className="action_value">{ERROR.DASH}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            )}
          </div>
          <div className="section_container safe_driving_night">
            <div className="section_container_header">
              <h3 className="title_xxsmall">{t('T20000')}</h3>
            </div>
            <p
              className="section_container_desc"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T20136')}
            </p>

            <ul className="statistics_info_list">
              <li className="statistics_info_item">
                <span
                  className="statistics_info_key"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('T20137')}
                </span>
                <span className="statistics_info_val">
                  <span className="statistics_info_number">{ERROR.DASH}</span>
                  <span className="statistics_info_unit">{t('T20133')}</span>
                </span>
              </li>
              <li className="statistics_info_item">
                <span
                  className="statistics_info_key"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('T20138')}
                </span>
                <span className="statistics_info_val">
                  <span className="statistics_info_number">{ERROR.DASH}</span>
                  <span className="statistics_info_unit">{t('T20133')}</span>
                </span>
              </li>
            </ul>
          </div>
          <div className="section_container safe_driving_ubi">
            <div className="section_container_header">
              <h3 className="title_xxsmall">{t('T20139')}</h3>
            </div>
            <p className="section_container_desc">
              {t('T20140_$score_$discount', {
                score: ERROR.DASH,
                discount: ERROR.DASH,
              })}
            </p>
            <div className="container_banner_imgbox">
              <ImageSelector
                alt="insurance_benefit_iocn"
                kiaLightSrc={() => import('@di-shared/assets/img/safe_driving_score/insure_benefit.png')}
                kiaDarkSrc={() => import('@di-shared/assets/img/safe_driving_score/insure_benefit.png')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/safe_driving_score/insure_benefit.png')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/safe_driving_score/insure_benefit.png')}
                genesisLightSrc={() => import('@di-shared/assets/img/safe_driving_score/insure_benefit.png')}
                genesisDarkSrc={() => import('@di-shared/assets/img/safe_driving_score/insure_benefit.png')}
              />
            </div>
            <div className="section_container_full_btn">
              <span className="blind">{t('T20141')}</span>
            </div>
          </div>
          <div className="section_container safe_driving_predict">
            <div className="section_container_header">
              <h3 className="title_xxsmall">{t('T20142')}</h3>
            </div>
            <p
              className="section_container_desc"
              id="scorePredictDescription"
              role="tabpanel"
              aria-labelledby="scorePredict500"
              aria-live="polite"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T20143_$distance_$score', {
                distance: `${ERROR.DASH}${defaultDistanceSymbol}`,
                score: ERROR.DASH,
              })}
            </p>
            <div className="score_predict">
              <span className="speech_bubble">
                <span className="blind">{t('T20144')}</span>
                <span className="score_predict_point">{t('T20006_$score', {score: ERROR.DASH})}</span>
                <span className="blind">{t('T20145')}</span>
                <span className="score_predict_distance">
                  {ERROR.DASH}
                  {defaultDistanceSymbol}
                </span>
              </span>
              <span
                className="predict_graph_bar"
                ref={predictGraphBarRef}
              >
                <span
                  className={clsx('bar_current')}
                  style={{width: predictBarWidth}}
                >
                  <span
                    className="speech_bubble"
                    aria-hidden="true"
                  >
                    <span className="score_predict_point">{t('T20006_$score', {score: ERROR.DASH})}</span>
                    <span className="score_predict_distance">
                      {ERROR.DASH}
                      {defaultDistanceSymbol}
                    </span>
                  </span>
                </span>
              </span>
              <div
                className="score_predict_control"
                role="tablist"
              >
                <button
                  type="button"
                  className="score_predict_btn"
                  id="scorePredictCurrent"
                  role="tab"
                  aria-selected="false"
                  aria-controls="scorePredictDescription"
                  disabled={true}
                >
                  {t('T20146')}
                </button>
                <button
                  ref={initialSelectedButtonRef}
                  type="button"
                  className="score_predict_btn"
                  id="scorePredict100"
                  role="tab"
                  aria-selected={predictIndex === 0}
                  aria-controls="scorePredictDescription"
                >
                  +{convertDistanceValue(100)}
                  {convertDistanceSymbol(100)}
                </button>
                <button
                  type="button"
                  className="score_predict_btn"
                  id="scorePredict300"
                  role="tab"
                  aria-selected={predictIndex === 1}
                  aria-controls="scorePredictDescription"
                >
                  +{convertDistanceValue(300)}
                  {convertDistanceSymbol(300)}
                </button>
                <button
                  type="button"
                  className="score_predict_btn"
                  id="scorePredict500"
                  role="tab"
                  aria-selected={predictIndex === 2}
                  aria-controls="scorePredictDescription"
                >
                  +{convertDistanceValue(500)}
                  {convertDistanceSymbol(500)}
                </button>
                <button
                  type="button"
                  className="score_predict_btn"
                  id="scorePredict1000"
                  role="tab"
                  aria-selected={predictIndex === 3}
                  aria-controls="scorePredictDescription"
                >
                  +{convertDistanceValue(1000)}
                  {convertDistanceSymbol(1000)}
                </button>
              </div>
            </div>
          </div>
          <div className="driving_banner type_square">
            <button
              type="button"
              className="driving_banner_box"
            >
              <span className="driving_banner_txtbox">
                <strong className="driving_banner_title body_large_b">{t('T20147')}</strong>
                <span
                  className="driving_banner_desc body_small_r"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('T20148')}
                </span>
              </span>
              <span className="driving_banner_imgbox">
                <ImageSelector
                  className="driving_banner_img"
                  alt="driving_banner_img"
                  kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_improve_service_light.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_improve_service.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_improve_service_light.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_improve_service.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_improve_service_light.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_improve_service.png')}
                />
              </span>
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}

export default SafeDrivingContentsErrorKr;
